// src/utils/pdfGeneratorExact.js
// Install: npm install jspdf html2canvas
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// EXACT HTML template using your actual images - MUCH EASIER!
const getInvoiceHTML = (quotationData) => {
    const itemsHTML = quotationData.items?.map(item => {
        const totalPrice = (item.quantity || 0) * (item.unitPrice || 0);
        return `
            <tr>
                <td>${item.itemNo || item.barcode || ''}</td>
                <td style="text-align: left; padding-left: 8px;">${item.description || ''}</td>
                <td>${item.quantity || 0}</td>
                <td>${(item.unitPrice || 0).toFixed(2)}</td>
                <td>${totalPrice.toFixed(2)}</td>
            </tr>
        `;
    }).join('') || '';

    const itemsCount = quotationData.items?.length || 0;
    const emptyRowsNeeded = Math.max(10 - itemsCount, 0);
    const emptyRowsHTML = Array(emptyRowsNeeded).fill().map(() => `
        <tr>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
        </tr>
    `).join('');

    const grandTotal = quotationData.items?.reduce((sum, item) =>
        sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0
    ) || 0;

    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <style>
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            
            body {
                font-family: Arial, sans-serif;
                background: white;
                color: black;
                width: 794px;
                height: 1123px;
                position: relative;
                overflow: hidden;
            }
            
            /* Header Image - NOT full width, same margins as content */
            .header-img {
                width: calc(100% - 60px);
                height: 120px;
                display: block;
                object-fit: contain;
                object-position: center top;
                margin: 0 30px 0 30px;
            }
            
            /* Title Bar - EXACT positioning */
            .title-bar {
                background: #00AEEF;
                color: white;
                text-align: center;
                font-size: 22px;
                font-weight: bold;
                padding: 15px 0;
                margin: 20px 30px 25px 30px;
                letter-spacing: 2px;
            }
            
            /* Table Container - EXACT spacing */
            .table-container {
                margin: 0 30px;
                position: relative;
            }
            
            /* Invoice Table - EXACT match to PDF */
            .invoice-table {
                width: 100%;
                border-collapse: collapse;
                background: white;
                border: 2px solid #333;
            }
            
            .invoice-table th {
                background: #666666;
                color: white;
                font-weight: bold;
                font-size: 13px;
                padding: 12px 6px;
                text-align: center;
                border: 1px solid #333;
                line-height: 1.2;
            }
            
            .invoice-table td {
                padding: 8px 6px;
                text-align: center;
                border: 1px solid #333;
                background: white;
                color: black;
                font-size: 11px;
                height: 30px;
                vertical-align: middle;
            }
            
            /* Column widths - EXACT from your PDF */
            .invoice-table th:nth-child(1),
            .invoice-table td:nth-child(1) { 
                width: 12%; 
            }
            
            .invoice-table th:nth-child(2),
            .invoice-table td:nth-child(2) { 
                width: 40%; 
                text-align: left;
                padding-left: 10px;
            }
            
            .invoice-table th:nth-child(3),
            .invoice-table td:nth-child(3) { 
                width: 12%; 
            }
            
            .invoice-table th:nth-child(4),
            .invoice-table td:nth-child(4) { 
                width: 18%; 
            }
            
            .invoice-table th:nth-child(5),
            .invoice-table td:nth-child(5) { 
                width: 18%; 
            }
            
            /* Grand Total - EXACT positioning */
            .grand-total-container {
                position: absolute;
                bottom: 100px;
                right: 30px;
                display: flex;
            }
            
            .grand-total-label {
                background: #666666;
                color: white;
                padding: 12px 20px;
                font-weight: bold;
                font-size: 14px;
                border: 2px solid #333;
                border-right: none;
            }
            
            .grand-total-value {
                background: #E6F3FF;
                color: #333;
                padding: 12px 25px;
                font-size: 16px;
                font-weight: bold;
                border: 2px solid #333;
                min-width: 140px;
                text-align: center;
            }
            
            /* Footer Image - EXACT positioning */
            .footer-img {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 60px;
                display: block;
                object-fit: contain;
                object-position: center bottom;
            }
            
            /* Content wrapper to avoid overlap */
            .content-wrapper {
                margin-bottom: 120px;
            }
        </style>
    </head>
    <body>
        <!-- Header Image - Your actual image -->
        <img class="header-img" src="/header.jpg" alt="UNITECH Header" />
        
        <!-- Title Bar -->
        <div class="title-bar">COMMERCIAL INVOICE</div>
        
        <!-- Content Wrapper -->
        <div class="content-wrapper">
            <!-- Invoice Table -->
            <div class="table-container">
                <table class="invoice-table">
                    <thead>
                        <tr>
                            <th>Item<br>No#</th>
                            <th>Description</th>
                            <th>QTY</th>
                            <th>Unit Price<br>USD</th>
                            <th>Total Price<br>USD</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsHTML}
                        ${emptyRowsHTML}
                    </tbody>
                </table>
            </div>
        </div>
        
        <!-- Grand Total -->
        <div class="grand-total-container">
            <div class="grand-total-label">GRAND TOTAL</div>
            <div class="grand-total-value">${grandTotal.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}</div>
        </div>
        
        <!-- Footer Image - Your actual image -->
        <img class="footer-img" src="/footer.jpg" alt="UNITECH Footer" />
    </body>
    </html>
    `;
};

// PDF Generator with perfect image handling
export const downloadQuotationPDFExact = async (quotationData) => {
    try {
        console.log('🚀 Creating PDF with your EXACT images...');
        
        const htmlContent = getInvoiceHTML(quotationData);
        
        // Create container with EXACT A4 dimensions
        const container = document.createElement('div');
        container.innerHTML = htmlContent;
        container.style.cssText = `
            position: fixed;
            top: -10000px;
            left: 0;
            width: 794px;
            height: 1123px;
            background: white;
            z-index: -1000;
        `;
        
        document.body.appendChild(container);
        
        // Wait for images to load properly
        console.log('⏳ Loading images...');
        const images = container.querySelectorAll('img');
        
        await Promise.all(Array.from(images).map(img => {
            return new Promise((resolve) => {
                if (img.complete && img.naturalWidth > 0) {
                    console.log(`✅ Image loaded: ${img.src}`);
                    resolve();
                } else {
                    img.onload = () => {
                        console.log(`✅ Image loaded: ${img.src}`);
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`⚠️ Image failed to load: ${img.src}`);
                        resolve(); // Continue anyway
                    };
                    // Timeout fallback
                    setTimeout(() => {
                        console.log(`⏰ Image timeout: ${img.src}`);
                        resolve();
                    }, 3000);
                }
            });
        }));
        
        // Extra delay for layout stabilization
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('📸 Capturing with html2canvas...');
        
        // Capture with optimal settings for your images
        const canvas = await html2canvas(container, {
            width: 794,
            height: 1123,
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            imageTimeout: 10000,
            foreignObjectRendering: false,
            onclone: (clonedDoc) => {
                console.log('🔄 Cloning document with images...');
                const body = clonedDoc.body;
                body.style.backgroundColor = 'white';
                body.style.width = '794px';
                body.style.height = '1123px';
                body.style.margin = '0';
                body.style.padding = '0';
                
                // Ensure images are properly sized in clone
                const clonedImages = clonedDoc.querySelectorAll('img');
                clonedImages.forEach(img => {
                    img.style.display = 'block';
                    img.style.maxWidth = '100%';
                });
            }
        });
        
        console.log('✅ Canvas created:', canvas.width, 'x', canvas.height);
        
        document.body.removeChild(container);
        
        // Validate canvas
        if (canvas.width === 0 || canvas.height === 0) {
            throw new Error('Canvas is empty - check if images are accessible');
        }
        
        // Create PDF
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        
        // Validate image data
        if (imgData === 'data:,' || imgData.length < 1000) {
            throw new Error('Invalid canvas data - images may not have loaded');
        }
        
        console.log('📄 Adding to PDF...');
        
        // Add to PDF with EXACT A4 dimensions
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
        
        // Generate filename
        const safeQuotationNumber = (quotationData.quotationNumber || 'Draft').replace(/[^a-zA-Z0-9-_]/g, '_');
        const filename = `UNITECH_Commercial_Invoice_${safeQuotationNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        pdf.save(filename);
        
        console.log('✅ PDF with images downloaded successfully:', filename);
        
        return { success: true, filename };
        
    } catch (error) {
        console.error('❌ PDF generation failed:', error);
        return { success: false, error: error.message };
    }
};

// Preview function to test images
export const previewInvoiceHTML = (quotationData) => {
    const htmlContent = getInvoiceHTML(quotationData);
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    console.log('🔍 Preview opened - check if header.jpg and footer.jpg load correctly');
};

